import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft,
  RefreshCw,
  Receipt,
  User,
  Mail,
  Hash,
  CreditCard,
  TrendingUp,
} from "lucide-react";
import { useAdminUserPaymentsQuery } from "../api/billingApi";
import { useUserInvoicesQuery } from "../api/billingApi";

function money(kobo: number) {
  return `₦${(kobo / 100).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const METHOD_LABELS: Record<string, string> = {
  bank_transfer: "Bank Transfer",
  cash: "Cash",
  pos: "POS",
};

export default function UserPaymentsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data: payments = [], isLoading, refetch, isFetching } =
    useAdminUserPaymentsQuery(userId!, { skip: !userId });

  const { data: invoiceData } = useUserInvoicesQuery(
    { userId: userId! },
    { skip: !userId }
  );
  const userData = invoiceData?.user;

  const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center gap-4">
        <RefreshCw className="w-10 h-10 text-primary animate-spin" />
        <span className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-400">
          Loading payments…
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10 py-12">

        {/* Header */}
        <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-10 mb-16">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-3 bg-white border border-slate-200 rounded-2xl shadow-sm text-slate-600 hover:text-primary transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              <nav className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                Payment History
              </nav>
            </div>

            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-[2rem] bg-slate-900 flex items-center justify-center text-white shadow-2xl shadow-slate-200">
                <User size={32} strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-4xl lg:text-5xl font-[1000] text-slate-900 tracking-tight leading-none uppercase">
                  {userData?.name ?? "Member"}
                  <span className="text-primary italic">.</span>
                </h1>
                <div className="flex flex-wrap gap-4 mt-3">
                  {userData?.email && (
                    <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400">
                      <Mail size={14} /> {userData.email}
                    </span>
                  )}
                  {userData?.membership_no && (
                    <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-widest">
                      <Hash size={14} /> {userData.membership_no}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 w-full xl:w-auto">
            <div className="bg-white rounded-[2.5rem] p-8 border-2 border-slate-900 shadow-xl flex-1 md:min-w-[260px]">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-2">
                <TrendingUp className="w-3 h-3" /> Total Paid
              </div>
              <div className="text-4xl font-[1000] text-slate-900 tracking-tighter">
                {money(totalPaid)}
              </div>
              <div className="text-[10px] font-bold text-slate-400 mt-1">
                {payments.length} transaction{payments.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </header>

        {/* Table */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-2 mb-4">
            <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
              All Payments ({payments.length})
            </h2>
            <button
              onClick={() => refetch()}
              className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline flex items-center gap-2"
            >
              <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {payments.length === 0 ? (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] p-32 text-center">
              <CreditCard className="w-16 h-16 mx-auto text-slate-200 mb-6" />
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">
                No payments recorded
              </p>
            </div>
          ) : (
            <div className="bg-white border border-slate-100 rounded-[2.5rem] overflow-hidden shadow-sm">
              {/* Column headers */}
              <div className="grid grid-cols-12 gap-4 px-8 py-4 bg-slate-50 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <div className="col-span-2">Paid At</div>
                <div className="col-span-3">Receipt / Reference</div>
                <div className="col-span-2">Method</div>
                <div className="col-span-2">Invoice</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-1 text-right">Amount</div>
                <div className="col-span-1 text-center">View</div>
              </div>

              {payments.map((p: any, i: number) => (
                <div
                  key={p.id}
                  className={`grid grid-cols-12 gap-4 px-8 py-5 items-center hover:bg-slate-50/60 transition-all ${
                    i < payments.length - 1 ? "border-b border-slate-50" : ""
                  }`}
                >
                  <div className="col-span-2 text-xs font-bold text-slate-500">
                    {p.date ?? "—"}
                  </div>

                  <div className="col-span-3 min-w-0">
                    <p className="text-xs font-black text-slate-900 truncate">
                      {p.receipt_number ?? "—"}
                    </p>
                    <p className="text-[10px] font-bold text-slate-400 truncate mt-0.5">
                      {p.reference}
                    </p>
                  </div>

                  <div className="col-span-2 text-xs font-bold text-slate-600">
                    {METHOD_LABELS[p.payment_method] ?? p.payment_method ?? "—"}
                  </div>

                  <div className="col-span-2 text-xs font-bold text-slate-500 truncate">
                    {p.invoice_number ?? <span className="text-slate-300">—</span>}
                  </div>

                  <div className="col-span-1">
                    {p.is_advance ? (
                      <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg">
                        Advance
                      </span>
                    ) : (
                      <span className="text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-1 rounded-lg">
                        Invoice
                      </span>
                    )}
                  </div>

                  <div className="col-span-1 text-right text-sm font-[900] text-slate-900 tabular-nums">
                    {money(p.amount)}
                  </div>

                  <div className="col-span-1 flex justify-center">
                    {p.reference && (
                      <Link
                        to={`/receipt/${p.receipt_number || p.reference}`}
                        className="p-2 rounded-xl bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-500 transition-all"
                        title="View Receipt"
                      >
                        <Receipt size={14} />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
